import { useEffect, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { MenuIcon, XIcon } from 'lucide-react';
import { DexButton } from '@dexmate/dexui';
import { useResponsive } from '@/lib/useResponsive';
import { Sidebar } from './Sidebar';
import { AppHeader } from './AppHeader';
import { AuthBanner } from './AuthBanner';
import { Breadcrumb } from './Breadcrumb';
import { SkipLink } from './SkipLink';
import { RoleDropdown } from './RoleDropdown';

/**
 * AppLayout — standalone-build shell. Mirrors dexhub's `AppLayout.tsx` shape
 * (sidebar + main content with sticky in-content header) but trimmed for
 * cloud-data-service.
 *
 * Layout (lg+):
 *   [Sidebar 240px][ flex-1
 *                    Sticky AppHeader
 *                    AuthBanner (when triggered)
 *                    Breadcrumb (when path depth >= 3)
 *                    <main id="main"> Outlet </main>
 *                  ]
 *
 * Mobile (< lg): sidebar collapses to a hamburger-triggered drawer; header
 * gains a menu button on the left.
 */
export function AppLayout() {
  const { isDesktop } = useResponsive();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change so navigation feels right on mobile.
  useEffect(() => {
    if (drawerOpen && isDesktop) setDrawerOpen(false);
  }, [drawerOpen, isDesktop]);

  return (
    <div className="min-h-screen bg-dex-bg-secondary text-dex-text-primary lg:flex">
      <SkipLink />

      {/* Desktop sidebar — always rendered, hidden via CSS below lg. */}
      <Sidebar />

      {/* Mobile drawer — overlay above content. */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/40"
          />
          <div className="relative h-full">
            <Sidebar mobile onNavigate={() => setDrawerOpen(false)} />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close navigation"
              className="absolute right-3 top-3 inline-flex size-8 items-center justify-center rounded-md text-dex-text-secondary hover:bg-dex-bg-tertiary"
            >
              <XIcon className="size-4" />
            </button>
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <AppHeader
          leading={
            !isDesktop ? (
              <DexButton
                variant="secondary"
                aria-label="Open navigation"
                onClick={() => setDrawerOpen(true)}
              >
                <MenuIcon className="size-4" />
              </DexButton>
            ) : null
          }
          identity={<RoleDropdown />}
        />

        <AuthBanner />
        <Breadcrumb />

        <main
          id="main"
          tabIndex={-1}
          className="flex-1 focus:outline-none"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// Re-export so other places that wrap a route can also pull layout-aware
// utilities without an extra import path.
export type AppLayoutChildren = ReactNode;
